import {
	clone,
	isDateBetween,
	numberToPercentage,
	roundNumber,
} from '@marmon/shared/types/common';
import {
	determineEightyTwenty,
	determineQuadByEightyTwenty,
} from '@marmon/shared/types/quad';
import {
	Analysis,
	CalculationId,
	DefaultProfitGroup,
	DefaultQuadProfit,
	Invoice,
	PeriodKey,
	ProfitTimePeriodGroup,
	QuadPeriodProfit,
	QuadProfitGroup,
	TimePeriodProfit,
} from '../interfaces';
import { generateCost, generateRevenue, percentChange } from './analysis.utils';
import { calculatePeriod } from './time-period.utils';

type QuadTimePeriodEntity = {
	[key in keyof QuadProfitGroup]: {
		total: Set<string>;
		timePeriods: [Set<string>, Set<string>];
	};
};

const DefaultEntityByQuad = (): QuadTimePeriodEntity => ({
	quad1: { total: new Set(), timePeriods: [new Set(), new Set()] },
	quad2: { total: new Set(), timePeriods: [new Set(), new Set()] },
	quad3: { total: new Set(), timePeriods: [new Set(), new Set()] },
	quad4: { total: new Set(), timePeriods: [new Set(), new Set()] },
	quadTotal: { total: new Set(), timePeriods: [new Set(), new Set()] },
});

/**
 * This function does not sort the total invoices into time periods, but instead generates
 * the data from the invoices assuming they are all from the same time period already.
 *
 * @param periodInvoices all of the invoices associated with the time period needing assessment
 * @returns
 */
export function generateQuadPeriodProfits(options: {
	analysis: Analysis;
	invoices: Invoice[];
	calculationId: CalculationId;
}): QuadProfitGroup {
	const { analysis, invoices, calculationId } = options;

	// first time period min and max
	const [firstPeriod] = analysis.timePeriods;
	const { minDate: min, maxDate: max } = firstPeriod;

	const value = DefaultQuadProfit();

	const customers: QuadTimePeriodEntity = DefaultEntityByQuad();
	const products: QuadTimePeriodEntity = DefaultEntityByQuad();

	invoices.forEach((invoice) => {
		const {
			invoiceDate,
			quads,
			revenueInCents,
			costInCents,
			customer,
			product,
		} = invoice;

		// fallback to 2nd period if not within the 1st
		const period = isDateBetween(invoiceDate, { min, max }) ? 0 : 1;

		const calculations = quads[calculationId];
		if (!calculations) {
			throw Error(
				'Cannot determine time periods, quads have not been calculated for invoice',
			);
		}

		const quad = determineQuadByEightyTwenty(
			determineEightyTwenty(calculations.customer),
			determineEightyTwenty(calculations.product),
		);

		const quadKey = `quad${quad}` as keyof QuadProfitGroup;
		const { timePeriods, total, comparisons } = value[quadKey];

		// Entity Sets
		customers.quadTotal.total.add(customer);
		customers.quadTotal.timePeriods[period].add(customer);

		products.quadTotal.total.add(product);
		products.quadTotal.timePeriods[period].add(product);

		customers[quadKey].total.add(customer);
		customers[quadKey].timePeriods[period].add(customer);

		products[quadKey].total.add(product);
		products[quadKey].timePeriods[period].add(product);

		// Model Periods
		timePeriods[period].transactionCount++;
		timePeriods[period].revenueInCents += revenueInCents;
		timePeriods[period].costInCents += costInCents;

		timePeriods[period].customerCount =
			customers[quadKey].timePeriods[period].size;
		timePeriods[period].productCount =
			products[quadKey].timePeriods[period].size;

		// Comparison Periods
		comparisons[period].transactionCount++;
		comparisons[period].revenueInCents += revenueInCents;
		comparisons[period].costInCents += costInCents;
		comparisons[period].customerCount =
			customers[quadKey].timePeriods[period].size;
		comparisons[period].productCount =
			products[quadKey].timePeriods[period].size;

		// Model Periods
		total.transactionCount++;
		total.revenueInCents += revenueInCents;
		total.costInCents += costInCents;
		total.customerCount = customers[quadKey].total.size;
		total.productCount = products[quadKey].total.size;

		// We need to apply the count to the quad totals here since a true aggregate later wouldn't
		// actually add up to the true unique customer and product counts since the same customer or
		// product can span across quads and time periods which will count to the total aggregate multiple
		// times
		value.quadTotal.total.customerCount = customers.quadTotal.total.size;
		value.quadTotal.total.productCount = products.quadTotal.total.size;

		value.quadTotal.timePeriods[period].customerCount =
			customers.quadTotal.timePeriods[period].size;
		value.quadTotal.timePeriods[period].productCount =
			products.quadTotal.timePeriods[period].size;

		value.quadTotal.comparisons[period].customerCount =
			customers.quadTotal.timePeriods[period].size;
		value.quadTotal.comparisons[period].productCount =
			products.quadTotal.timePeriods[period].size;
	});

	return applyTotalQuadCalculation(value, analysis);
}

/**
 * Applies the "Phase 1: Pricing Adjustments & Overhead Redistribution" method to the provided
 * period profit data.
 *
 * Step 1: Find the CM% differences amongst the quads in comparison to the first quad. ( 0 / +7 / +7 / +15 )
 * Step 2: Increase quad CM% to meet the requirement (if needed).
 * Step 3: Apply the CM$ difference from after "Step 2" back onto the quads (increasing the quads CM$)
 *
 * @param data the data to apply the phase 1 method to
 */
export function applyPhase1Quads(data: QuadPeriodProfit[], analysis: Analysis) {
	const quads = cloneQuads(data);

	const { quad1, quad2, quad3, quad4 } = quads;

	if (!quad1 || !quad2 || !quad3 || !quad4 || !quads.quadTotal)
		throw Error('Required quads were not provided');

	applyMarginDifference(quad2, quad1);
	applyMarginDifference(quad3, quad1);
	applyMarginDifference(quad4, quad1);

	applyOverheadDistributionTargets(quad1, quads.quadTotal);
	applyOverheadDistributionTargets(quad2, quads.quadTotal);
	applyOverheadDistributionTargets(quad3, quads.quadTotal);
	applyOverheadDistributionTargets(quad4, quads.quadTotal);

	const value = applyTotalQuadCalculation(quads, analysis, {
		persistOverheadInCents: true,
	});

	return [
		value.quad1,
		value.quad2,
		value.quad3,
		value.quad4,
		value.quadTotal,
	];
}

/**
 * Methodology premium ranges vs Quad 1, as ratios (0.07 = +7 pts).
 * Quad 1 is the market-price baseline. Calculations use the max of each range.
 */
export const QuadMarginPremiumPointRanges = {
	quad1: { min: 0, max: 0 },
	quad2: { min: 0.05, max: 0.07 },
	quad3: { min: 0.05, max: 0.07 },
	quad4: { min: 0.1, max: 0.15 },
} as const;

/**
 * Margin premiums vs Quad 1, in percentage points (0.07 = +7 pts).
 * Quad 1 is the market-price baseline. These are the range ceilings.
 */
export const QuadMarginPremiumPointTargets = {
	quad1: QuadMarginPremiumPointRanges.quad1.max,
	quad2: QuadMarginPremiumPointRanges.quad2.max,
	quad3: QuadMarginPremiumPointRanges.quad3.max,
	quad4: QuadMarginPremiumPointRanges.quad4.max,
} as const;

/**
 * Expected revenue / overhead mix across quads.
 */
export const QuadExpectedShareTargets = {
	quad1: 0.64,
	quad2: 0.16,
	quad3: 0.16,
	quad4: 0.04,
} as const;

/**
 * The +targets that the margin needs to hit in comparison to the quad margin percent.
 *
 * @example quad1 has a marginPercent of 0.3 so the subsequent quads need 0.37/0.37/0.45
 * in order to be compliant. If they do not hit those values then we need to take the difference
 * and reapply it back to their marginInCents and revenueInCents
 */
const MarginTargets: { [key in ProfitTimePeriodGroup]?: number } = {
	quad2: QuadMarginPremiumPointTargets.quad2,
	quad3: QuadMarginPremiumPointTargets.quad3,
	quad4: QuadMarginPremiumPointTargets.quad4,
} as const;

function applyMarginDifference(
	period: QuadPeriodProfit,
	quad1: QuadPeriodProfit,
) {
	const target = MarginTargets[period.key] ?? null;
	if (!target) throw Error('Margin target could not be determined');

	const checkCompliance = (
		timePeriod: TimePeriodProfit,
		timePeriodQuad1: TimePeriodProfit,
	) => {
		const periodMarginPercent = timePeriod.marginPercent ?? 0;

		const marginDiff =
			periodMarginPercent - (timePeriodQuad1.marginPercent ?? 0);
		const isCompliant = marginDiff >= target;

		// matches or exceeds the target, so no adjustments
		if (isCompliant) return timePeriod;

		const targetDiff = target - marginDiff;
		const marginPercent = numberToPercentage(
			periodMarginPercent + targetDiff,
		);

		return {
			...timePeriod,
			// applies new revenue to the time period based on the new margin percent
			revenueInCents: generateRevenue(timePeriod, marginPercent),
		};
	};

	period.total = checkCompliance(period.total, quad1.total);
	period.timePeriods = period.timePeriods.map((p, index) =>
		checkCompliance(p, quad1.timePeriods[index]),
	) as [TimePeriodProfit, TimePeriodProfit];
}

/**
 * The overhead distribution that needs to be applied during phase 1 across the detailed quads
 */
const OverheadTargets: { [key in ProfitTimePeriodGroup]?: number } = {
	quad1: QuadExpectedShareTargets.quad1,
	quad2: QuadExpectedShareTargets.quad2,
	quad3: QuadExpectedShareTargets.quad3,
	quad4: QuadExpectedShareTargets.quad4,
};

function applyOverheadDistributionTargets(
	period: QuadPeriodProfit,
	quadTotal: QuadPeriodProfit,
) {
	const target = OverheadTargets[period.key] ?? null;
	if (!target) throw Error('Overhead Target could not be determined');

	const applyOverheadDistribution = (
		total: TimePeriodProfit,
		timePeriod: TimePeriodProfit,
	) => ({
		...timePeriod,
		overheadInCents: total.overheadInCents * target,
	});

	period.total = applyOverheadDistribution(quadTotal.total, period.total);

	period.timePeriods[0] = applyOverheadDistribution(
		quadTotal.timePeriods[0],
		period.timePeriods[0],
	);

	period.timePeriods[1] = applyOverheadDistribution(
		quadTotal.timePeriods[1],
		period.timePeriods[1],
	);
}

export function applyPhase2Quads(data: QuadPeriodProfit[], analysis: Analysis) {
	const quads = applyPhase1Quads(data, analysis);
	const [quad1, quad2, quad3, quad4, quadTotal] = quads;

	function applyIncreases(period: TimePeriodProfit) {
		const marginPercent = (period.marginPercent ?? 0) + 0.01;
		const revenueInCents = period.revenueInCents * 1.1;
		return {
			...period,
			marginPercent,
			revenueInCents,
			costInCents: generateCost(revenueInCents, marginPercent),
		};
	}

	quad1.total = applyIncreases(quad1.total);
	quad1.timePeriods = quad1.timePeriods.map((p) => applyIncreases(p)) as [
		TimePeriodProfit,
		TimePeriodProfit,
	];

	const value = applyTotalQuadCalculation(
		{ quad1, quad2, quad3, quad4, quadTotal },
		analysis,
		{ persistOverheadInCents: true },
	);

	return [
		value.quad1,
		value.quad2,
		value.quad3,
		value.quad4,
		value.quadTotal,
	];
}

function applyTotalQuadCalculation(
	value: QuadProfitGroup,
	analysis: Analysis,
	options?: { persistOverheadInCents: boolean },
) {
	const { persistOverheadInCents } = {
		persistOverheadInCents: false,
		...options,
	};

	const { quad1, quad2, quad3, quad4 } = value;

	value.quadTotal = generateQuadTotals([quad1, quad2, quad3, quad4], {
		quadTotalPrev: value.quadTotal,
		analysis,
	});

	const { total, timePeriods } = value.quadTotal;
	Object.entries(value).forEach(([key, profit]) => {
		// already calculated at this point
		if (key === 'quadTotal') return;

		profit.total = calculatePeriod(profit.total, {
			forcedOverheadInCents: persistOverheadInCents
				? profit.total.overheadInCents
				: undefined,
			totalOperatingIncomeInCents: total.operatingIncomeInCents,
			totalTransactionCount: total.transactionCount,
			totalOverheadInCents: total.overheadInCents,
			totalRevenueInCents: total.revenueInCents,
		});

		profit.timePeriods = profit.timePeriods.map((period, index) =>
			calculatePeriod(period, {
				forcedOverheadInCents: persistOverheadInCents
					? period.overheadInCents
					: undefined,
				totalOperatingIncomeInCents:
					timePeriods[index].operatingIncomeInCents,
				totalTransactionCount: timePeriods[index].transactionCount,
				totalOverheadInCents: timePeriods[index].overheadInCents,
				totalRevenueInCents: timePeriods[index].revenueInCents,
			}),
		) as [TimePeriodProfit, TimePeriodProfit];

		profit.comparisons = profit.comparisons.map((period) =>
			calculatePeriod(period, {
				forcedOverheadInCents: persistOverheadInCents
					? period.overheadInCents
					: undefined,
				totalOperatingIncomeInCents:
					profit.total.operatingIncomeInCents,
				totalTransactionCount: profit.total.transactionCount,
				totalOverheadInCents: profit.total.overheadInCents,
				totalRevenueInCents: profit.total.revenueInCents,
			}),
		) as [TimePeriodProfit, TimePeriodProfit];
	});

	return value;
}

/**
 * Generates the quad total based on the values of all other quads and returns
 * a PeriodProfit data object.
 *
 * @param quads the quads to use for aggregation into the total quad
 * @param overheadInCents the total overhead costs for the analysis
 */
function generateQuadTotals(
	quads: QuadPeriodProfit[],
	options: {
		quadTotalPrev: QuadPeriodProfit;
		analysis: Analysis;
	},
) {
	const { quadTotalPrev, analysis } = options;
	const quadTotal = DefaultProfitGroup(
		'quad',
		'quadTotal',
	) as QuadPeriodProfit;

	// reapply the entity counts since they cannot be recalculated based on the individual
	// quads and time periods since they are unique
	quadTotal.comparisons = quadTotal.comparisons.map((c, i) => ({
		...c,
		customerCount: quadTotalPrev.comparisons[i].customerCount,
		productCount: quadTotalPrev.comparisons[i].productCount,
	})) as [TimePeriodProfit, TimePeriodProfit];

	quadTotal.timePeriods = quadTotal.timePeriods.map((p, i) => ({
		...p,
		customerCount: quadTotalPrev.timePeriods[i].customerCount,
		productCount: quadTotalPrev.timePeriods[i].productCount,
	})) as [TimePeriodProfit, TimePeriodProfit];

	quadTotal.total.customerCount = quadTotalPrev.total.customerCount;
	quadTotal.total.productCount = quadTotalPrev.total.productCount;

	quads.forEach(({ total, timePeriods, comparisons }) => {
		quadTotal.total.revenueInCents += total.revenueInCents;
		quadTotal.total.costInCents += total.costInCents;
		quadTotal.total.transactionCount += total.transactionCount;

		timePeriods.forEach((p, index) => {
			quadTotal.timePeriods[index].revenueInCents += p.revenueInCents;
			quadTotal.timePeriods[index].costInCents += p.costInCents;
			quadTotal.timePeriods[index].transactionCount += p.transactionCount;
		});

		comparisons.forEach((c, index) => {
			quadTotal.comparisons[index].revenueInCents += c.revenueInCents;
			quadTotal.comparisons[index].costInCents += c.costInCents;
			quadTotal.comparisons[index].transactionCount += c.transactionCount;
		});
	});

	// timePeriods and comparisons resemble each other in quadTotal
	quadTotal.timePeriods = quadTotal.timePeriods.map((p, index) => {
		const totalTransactionCount = p.transactionCount;
		const totalOverheadInCents = roundNumber(
			totalTransactionCount *
				analysis.timePeriods[index].overheadCostInCentsPerTransaction,
		);

		return calculatePeriod(p, {
			totalTransactionCount,
			totalOverheadInCents,
			totalRevenueInCents: p.revenueInCents,
		});
	}) as [TimePeriodProfit, TimePeriodProfit];

	// timePeriods and comparisons resemble each other in quadTotal
	quadTotal.comparisons = quadTotal.comparisons.map((p, index) => {
		const totalTransactionCount = p.transactionCount;
		const totalOverheadInCents = roundNumber(
			totalTransactionCount *
				analysis.timePeriods[index].overheadCostInCentsPerTransaction,
		);

		return calculatePeriod(p, {
			totalTransactionCount,
			totalOverheadInCents,
			totalRevenueInCents: p.revenueInCents,
		});
	}) as [TimePeriodProfit, TimePeriodProfit];

	const totalOverheadInCents = quadTotal.comparisons.reduce((c, a, index) => {
		const totalTransactionCount = a.transactionCount;
		c += roundNumber(
			totalTransactionCount *
				analysis.timePeriods[index].overheadCostInCentsPerTransaction,
		);
		return c;
	}, 0);

	quadTotal.total = calculatePeriod(quadTotal.total, {
		totalOverheadInCents,
		totalTransactionCount: quadTotal.total.transactionCount,
		totalRevenueInCents: quadTotal.total.revenueInCents,
	});

	return quadTotal;
}

const cloneQuads = (data: QuadPeriodProfit[]) =>
	({
		quad1: clone(data.find((d) => d.key === 'quad1')),
		quad2: clone(data.find((d) => d.key === 'quad2')),
		quad3: clone(data.find((d) => d.key === 'quad3')),
		quad4: clone(data.find((d) => d.key === 'quad4')),
		quadTotal: clone(data.find((d) => d.key === 'quadTotal')),
	}) as QuadProfitGroup;

export const createPeriodChange = ([first, second]: [
	TimePeriodProfit,
	TimePeriodProfit,
]) =>
	Object.entries(first).reduce((value, [key, firstValue]) => {
		const secondValue = second[key as PeriodKey];

		let difference = 0;
		if (firstValue === null && secondValue === null) {
			difference = 0;
		} else if (firstValue === null) {
			difference = secondValue as number;
		} else if (secondValue === null) {
			difference = firstValue as number;
		} else {
			difference = (secondValue ?? 0) - firstValue;
		}

		value[key as PeriodKey] = difference;
		return value;
	}, {} as TimePeriodProfit);

export const createPeriodChangePercent = ([first, second]: [
	TimePeriodProfit,
	TimePeriodProfit,
]) =>
	Object.entries(first).reduce((value, [key, firstValue]) => {
		const secondValue = second[key as PeriodKey];

		let change = 0;
		if (firstValue === null && secondValue === null) {
			change = 0;
		} else if (firstValue === null) {
			change = percentChange(0, secondValue as number);
		} else if (secondValue === null) {
			change = percentChange(firstValue as number, 0);
		} else {
			change = percentChange(firstValue as number, secondValue as number);
		}

		value[key as PeriodKey] = change;
		return value;
	}, {} as TimePeriodProfit);
